import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AnalyticsService } from '@common/services/analytics.service';

import { LINE_DONE_COPY } from '../../constants/credit-line.constants';
import { frecuenciaLabel } from '../../constants/credito-terms.constants';
import { CreditLine, Credito } from '../../models';
import { trackCreditEvent } from '../../utils/credit-analytics.util';
import { formatLineUsd } from '../../utils/line-format.util';
import { levelRequirements, LineLadderStep, lineLadderSteps, loansMissingForNext, visibleLineMark } from '../../utils/line-presentation.util';

/**
 * Modal de préstamo pagado o subida de nivel de la línea v2 (FINA-3378). Lo monta `AppComponent` en
 * cualquier ruta cuando el vigía global (`LineMilestoneWatcherService`) ve el último crédito
 * `completed`, una sola vez por crédito (`cr_line_done_seen_<creditoId>`). Por eso vive en
 * `CreditosSharedModule` (eager). Reemplaza en la v2 al `completion-modal` de las ofertas.
 *
 * Dos variantes: subió de nivel (`line.level > credito.level`) o no. Con la línea apagada no se abre.
 * Lo que falta para el siguiente nivel sale de `levelRequirements` (lo que decide el motor), nunca
 * de una cuenta propia del cupo.
 */
@Component({
    selector: 'fina-line-done-modal',
    templateUrl: './line-done-modal.component.html',
    styleUrls: ['./line-done-modal.component.scss'],
})
export class LineDoneModalComponent implements OnInit {
    @Input() line: CreditLine | null = null;
    @Input() credito: Pick<Credito, 'level'> | null = null;

    /** Cerrar: X, fondo, o «Pedir mi próximo préstamo» sin `canRequest`. */
    @Output() closed = new EventEmitter<void>();
    /** «Pedir mi próximo préstamo» con `canRequest`: el Home retira el modal y navega a Fina Presta. */
    @Output() requestNext = new EventEmitter<void>();
    /** «Entendido» (no subió de nivel): el Home retira el modal (FINA-3378). */
    @Output() acknowledged = new EventEmitter<void>();

    readonly copy = LINE_DONE_COPY;

    constructor(private analyticsService: AnalyticsService) {}

    /** El vigía lo monta una sola vez por crédito: montarse es la vista (FINA-3412). */
    ngOnInit(): void {
        trackCreditEvent(this.analyticsService, 'lineDoneModalViewed', {
            ...this.milestoneProps(),
            can_request: this.canRequest,
            at_max_level: !!this.line && !this.line.nextLevel,
            has_mark: !!visibleLineMark(this.line),
        });
    }

    private milestoneProps(): { variant: 'level_up' | 'paid'; level: number | null } {
        return { variant: this.leveledUp ? 'level_up' : 'paid', level: this.line?.level ?? null };
    }

    get leveledUp(): boolean {
        return (Number(this.line?.level) || 0) > (Number(this.credito?.level) || 0);
    }

    get canRequest(): boolean {
        return !!this.line?.canRequest;
    }

    get cupo(): string {
        return formatLineUsd(this.line?.cupo?.amountUSD);
    }

    get title(): string {
        return this.leveledUp ? this.copy.levelUpTitle(Number(this.line?.level) || 0) : this.copy.paidTitle;
    }

    /** Texto principal (prototipo): la subida de nivel o el cupo y lo que falta para el siguiente. */
    get body(): string {
        if (this.leveledUp) {
            const terms = this.line?.terms;
            const count = Number(terms?.installmentsCount) || 0;
            return this.copy.levelUpBody(this.cupo, count, frecuenciaLabel(terms?.installmentIntervalDays, count !== 1));
        }
        return `${this.copy.paidBody(this.cupo)} ${this.missingText}`;
    }

    /**
     * Lo que falta para el siguiente nivel, en este orden: nivel máximo, marca activa (no se sube
     * con marca), ya cumple, o cuántos préstamos más a tiempo (`loansMissingForNext`, máximo
     * conservador con los números del motor).
     */
    get missingText(): string {
        const line = this.line;
        const next = line?.nextLevel;
        if (!line || !next) return this.copy.maxLevel;
        if (visibleLineMark(line)) return this.copy.markPending;
        if (levelRequirements(line, next).allMet) return this.copy.allMet(next.level);
        return this.copy.remaining(next.level, loansMissingForNext(line));
    }

    /** Cómo levantar la marca; con la línea castigada no se muestra (DT-30). */
    get markNote(): string | null {
        const mark = visibleLineMark(this.line);
        return mark ? this.copy.markNote(mark.type, mark.needed) : null;
    }

    /** Mini escalera (`lineLadderSteps`, la misma de la bienvenida); sin `levels`, vacía. */
    get steps(): LineLadderStep[] {
        return lineLadderSteps(this.line);
    }

    trackByLevel(_: number, step: LineLadderStep): number {
        return step.level;
    }

    /** CTA del prototipo: «Pedir mi próximo préstamo» al subir de nivel; si no, «Entendido». Pedir solo con `canRequest`. */
    get ctaLabel(): string {
        return this.leveledUp ? this.copy.levelUpCta : this.copy.ok;
    }

    onCta(): void {
        const action = !this.leveledUp ? 'acknowledged' : this.canRequest ? 'request_next' : 'closed';
        trackCreditEvent(this.analyticsService, 'lineDoneModalCtaClicked', { action, ...this.milestoneProps() });
        if (action === 'acknowledged') this.acknowledged.emit();
        else if (action === 'request_next') this.requestNext.emit();
        else this.closed.emit();
    }

    /** X o fondo. */
    close(source: 'close_button' | 'backdrop'): void {
        trackCreditEvent(this.analyticsService, 'lineDoneModalDismissed', { dismiss_source: source, ...this.milestoneProps() });
        this.closed.emit();
    }
}
